import { useEffect, useState } from "react";
import { ActivityIndicator, ScrollView } from "react-native";
import { useLocalSearchParams } from "expo-router";
import { Text, View } from "@src/components";
import { sizes } from "@src/constants/sizes";
import { isWeb } from "@src/utils/web-helpers";
import {
  getWasteSenseReport,
  getWasteSenseSiteLocations,
  RANDOM_FIXTURE_ID,
  WasteSenseFixtureNotFoundError,
} from "@src/features/wastesense/api";
import type { WasteSenseMapSite, WasteSenseReport } from "@src/features/wastesense/types";
import { computeSummaryStats, formatReportingWindow } from "@src/features/wastesense/summary";
import { defaultImageProvider } from "@src/features/wastesense/imageProvider";
import { generateSeedToken } from "@src/features/wastesense/random/prng";
import { sortSites } from "@src/features/wastesense/siteDisplay";
import { WasteSenseHero } from "@src/features/wastesense/components/WasteSenseHero";
import { PriorityIncidentsHero } from "@src/features/wastesense/components/PriorityIncidentsHero";
import { WeeklySummary } from "@src/features/wastesense/components/WeeklySummary";
import { PriorityMap } from "@src/features/wastesense/components/PriorityMap";
import { SiteIncidentLogs } from "@src/features/wastesense/components/SiteIncidentLogs";

export default function WasteSenseReportScreen() {
  const { fixture, seed } = useLocalSearchParams<{ fixture?: string; seed?: string }>();
  const [report, setReport] = useState<WasteSenseReport | null>(null);
  const [mapSites, setMapSites] = useState<readonly WasteSenseMapSite[]>([]);
  const [error, setError] = useState<string | null>(null);

  // For the random fixture we need a seed to fetch with on the very
  // first render. Honour the URL's `?seed=` when present; otherwise
  // mint one once and keep it stable for the lifetime of this screen.
  // We deliberately don't write it back to the URL — `router.replace`
  // races the navigator's first mount and `window.history.replaceState`
  // adds plumbing we don't need. The seed lives in component state;
  // anyone wanting reproducibility can copy/paste from the list view's
  // generated link, which already includes a seed.
  const [mintedSeed] = useState<string>(() => generateSeedToken());
  const effectiveSeed = fixture === RANDOM_FIXTURE_ID ? (seed ?? mintedSeed) : seed;
  if (fixture === RANDOM_FIXTURE_ID) console.log("effectiveSeed", effectiveSeed);

  useEffect(() => {
    if (!isWeb) return;

    let cancelled = false;
    setError(null);
    setReport(null);
    setMapSites([]);
    Promise.all([getWasteSenseReport(fixture, effectiveSeed), getWasteSenseSiteLocations(fixture, effectiveSeed)])
      .then(([r, locations]) => {
        if (cancelled) return;
        setReport(r);
        setMapSites(locations);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof WasteSenseFixtureNotFoundError) {
          setError(`No report found for "${fixture}".`);
        } else if (e instanceof Error) {
          setError(e.message);
        } else {
          setError("Failed to load report");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [fixture, effectiveSeed]);

  if (!isWeb) {
    return (
      <View flex={1} alignItems="center" justifyContent="center" padding="3xl" gap="md">
        <Text textStyle="sm/semibold" color="text" textAlign="center">
          WasteSense reports are only available on the web client.
        </Text>
        <Text textStyle="xs/regular" color="textMuted" textAlign="center">
          Open this report in your browser to view it.
        </Text>
      </View>
    );
  }

  // Parent `app/reports/_layout.web.tsx` renders the WebLayout chrome.
  return (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={{
        paddingTop: 40,
        paddingBottom: 40,
        paddingHorizontal: 40,
        maxWidth: sizes.webReportMaxWidth,
        width: "100%",
      }}
      showsVerticalScrollIndicator={false}
    >
      {error ?
        <View padding="3xl" alignItems="center">
          <Text textStyle="sm/regular" color="textMuted">
            {error}
          </Text>
        </View>
      : !report ?
        <View padding="3xl" alignItems="center" gap="md">
          <ActivityIndicator />
          <Text textStyle="sm/regular" color="textMuted">
            Loading report…
          </Text>
        </View>
      : <ReportContent report={report} mapSites={mapSites} />}
    </ScrollView>
  );
}

function ReportContent({ report, mapSites }: { report: WasteSenseReport; mapSites: readonly WasteSenseMapSite[] }) {
  const { data } = report;
  const stats = computeSummaryStats(data);
  const window = formatReportingWindow(data);
  const tenantName = data.tenant?.name ?? "Tenant";
  // Order sites by suburb (then address) once here so the priority
  // map list and the per-site incident logs share the same sequence.
  const orderedSites = sortSites(data.sites);

  // Hero → map focus plumbing. The hero asks the map to focus a site
  // by bumping `focusRequest.key`; the map effect re-runs even when
  // the same site is clicked twice in a row.
  const [focusRequest, setFocusRequest] = useState<{ assetId: string; key: number } | null>(null);
  const handleLocateOnMap = (assetId: string) => {
    setFocusRequest((prev) => ({ assetId, key: (prev?.key ?? 0) + 1 }));
    /**
     * Bring the map into view so the user can see the focus take
     * effect. `scrollIntoView` walks up to the nearest scrolling
     * ancestor (the report's outer ScrollView), which on web renders
     * as a regular overflow:auto div. `block: "start"` lands the map
     * heading near the top of the viewport rather than mid-screen.
     */
    if (typeof document !== "undefined") {
      document.getElementById(PRIORITY_MAP_ANCHOR_ID)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <View gap="6xl">
      <WasteSenseHero tenantName={tenantName} reportingWindow={window} />
      <PriorityIncidentsHero
        sites={orderedSites}
        imageProvider={defaultImageProvider}
        onLocateOnMap={handleLocateOnMap}
      />
      <WeeklySummary stats={stats} />
      <View nativeID={PRIORITY_MAP_ANCHOR_ID}>
        <PriorityMap sites={orderedSites} mapSites={mapSites} focusRequest={focusRequest} />
      </View>
      <SiteIncidentLogs sites={orderedSites} imageProvider={defaultImageProvider} />
    </View>
  );
}

/**
 * DOM id used by the hero's "Locate on map" handler to scroll the
 * report to the priority map. Generated once per module load with a
 * random suffix so the id is guaranteed unique against anything else
 * on the page (the report mounts inside the wider app shell, and we
 * don't want to collide with another component reaching for a
 * generic id like `priority-map`).
 */
const PRIORITY_MAP_ANCHOR_ID = `wastesense-priority-map-${Math.random().toString(36).slice(2, 10)}`;
